import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BugsnagService } from '../appServices/bugsnag.service';

/**
 * BugsnagHttpInterceptor
 * ──────────────────────
 * Automatically catches every failed HTTP request and reports it to Bugsnag
 * with full context: URL, method, status code, and request payload.
 *
 * Registration: add to providers in main.ts (see instructions at bottom of file).
 */
@Injectable()
export class BugsnagHttpInterceptor implements HttpInterceptor {

    // These status codes are expected / handled elsewhere — skip reporting them
    private readonly IGNORED_STATUS_CODES = [
        // 401,   // unauthenticated  – handled by login redirect
        // 403,   // forbidden        – handled by route guards
        // 404,   // not found        – often expected (feature flags, optional endpoints)
        // 422,   // validation error – handled by form components
    ];

    constructor(private bugsnagService: BugsnagService) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {

                if (!this.IGNORED_STATUS_CODES.includes(error.status)) {

                    // ── Sanitize headers — strip token to avoid leaking credentials ──
                    const sanitizedHeaders: Record<string, string> = {};
                    req.headers.keys().forEach(key => {
                        const lowerKey = key.toLowerCase();
                        if (lowerKey === 'authorization' || lowerKey === 'token') {
                            sanitizedHeaders[key] = '[REDACTED]';
                        } else {
                            sanitizedHeaders[key] = req.headers.get(key) ?? '';
                        }
                    });

                    this.bugsnagService.notifyApiError(
                        error,
                        req.urlWithParams,
                        req.method,
                        req.body ?? {},
                        sanitizedHeaders
                    );
                }

                // Always re-throw so components can still handle the error locally
                return throwError(() => error);
            })
        );
    }
}